import { useEffect, useState } from 'react';
import { Modal, Form, Select, Alert, Button, Spin, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { api } from '../../shared/api/api';
import { useRootStore } from '../../app/providers/StoreProvider';
import {
  PLATFORM_LOCALES,
  LOCALE_LABEL,
} from '../../shared/constants/platform-locales';

/**
 * Модалка «Локализация платформы».
 *
 * Платформенный админ сам выбирает:
 *  - какие языки включены на его объекте (languages: string[])
 *  - какой язык — по умолчанию для гостей (default_language: string)
 *
 * PATCH уходит в /api/v1/platforms/<id>. Backend ограничивает набор полей
 * PlatformSerializer-ом, а SlvnViewMixin сам фильтрует платформу по current_platform —
 * чужие объекты сохранить не получится.
 */
export const LocalizationModal = observer(function LocalizationModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const rootStore = useRootStore();
  const activePlatformId = rootStore.platforms.activePlatformId;

  const [form] = Form.useForm<{ languages: string[]; default_language: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ─────── Загружаем актуальные languages при открытии ─────── */
  useEffect(() => {
    if (!open || !activePlatformId) return;

    let cancelled = false;
    setLoading(true);

    api
      .get(`/api/v1/platforms/${activePlatformId}`)
      .then((r) => {
        if (cancelled) return;
        const raw: string[] = Array.isArray(r.data?.languages)
          ? r.data.languages
          : [];
        const langs = raw.length > 0 ? raw : ['ru-RU'];
        const def =
          r.data?.default_language && langs.includes(r.data.default_language)
            ? r.data.default_language
            : langs[0];
        form.setFieldsValue({ languages: langs, default_language: def });
      })
      .catch(() => {
        if (!cancelled) message.error('Не удалось загрузить языки объекта');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, activePlatformId, form]);

  /* ─────── Сохранение ─────── */
  const handleSave = async () => {
    if (!activePlatformId) return;
    let values: { languages: string[]; default_language: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    // Инвариант: default_language ∈ languages. Чиним на всякий случай.
    if (!values.languages.includes(values.default_language)) {
      values.default_language = values.languages[0];
    }

    setSaving(true);
    try {
      const { data } = await api.patch(
        `/api/v1/platforms/${activePlatformId}`,
        values,
      );
      // Обновляем локальный кэш, чтобы шапка / сайдбар мгновенно
      // увидели новый default_language (например для переключателя локали).
      // Платформенный стор хранит поля в свободной схеме ([key: string]: any),
      // поэтому кастуем запись к Partial<Platform>.
      rootStore.platforms.updatePlatform(activePlatformId, {
        languages: data?.languages ?? values.languages,
        default_language: data?.default_language ?? values.default_language,
      });
      message.success('Настройки локализации сохранены');
      onClose();
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ||
        (typeof e?.response?.data === 'object'
          ? JSON.stringify(e.response.data)
          : null) ||
        e?.message ||
        'Неизвестная ошибка';
      message.error(`Ошибка сохранения: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Локализация объекта"
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={520}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Отмена
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
          disabled={loading}
        >
          Сохранить
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Выбранные языки будут доступны гостям в Консьерже, онлайн чек-ине и на публичных страницах. Язык по умолчанию используется, если язык гостя ещё не определён."
          />
          <Form form={form} layout="vertical" preserve={false}>
            <Form.Item
              name="languages"
              label="Языки объекта"
              rules={[
                {
                  validator: (_r, v: string[]) =>
                    Array.isArray(v) && v.length > 0
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error('Выберите хотя бы один язык'),
                        ),
                },
              ]}
            >
              <Select
                mode="multiple"
                options={PLATFORM_LOCALES.map((l) => ({
                  value: l.value,
                  label: l.label,
                }))}
                placeholder="Выберите языки"
                onChange={(next: string[]) => {
                  const current = form.getFieldValue('default_language');
                  if (!current || !next.includes(current)) {
                    form.setFieldValue(
                      'default_language',
                      next[0] ?? undefined,
                    );
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.languages !== c.languages}
            >
              {() => {
                const langs: string[] =
                  form.getFieldValue('languages') || [];
                return (
                  <Form.Item
                    name="default_language"
                    label="Язык по умолчанию"
                    rules={[
                      {
                        required: true,
                        message: 'Укажите язык по умолчанию',
                      },
                    ]}
                  >
                    <Select
                      options={langs.map((loc) => ({
                        value: loc,
                        label: LOCALE_LABEL[loc] ?? loc,
                      }))}
                      placeholder="Выберите язык по умолчанию"
                      disabled={langs.length === 0}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
});
